import type { SearchFormData } from "./store/types";
import {
  getFromLocalStorage,
  setToLocalStorage,
  removeFromLocalStorage,
} from "@/utils/localStorage";

const SEARCH_FORM_STORAGE_KEY = "cab-recent-search";

/**
 * Save search form data to localStorage
 * @param formData - The search form data to save
 * @returns true if saved successfully, false otherwise
 */
export const saveSearchFormData = (formData: SearchFormData): boolean => {
  return setToLocalStorage(SEARCH_FORM_STORAGE_KEY, formData);
};

/**
 * Load data from localStorage without validation
 * @returns The raw data from localStorage or null if not found
 */
const loadDataFromStorage = (): unknown => {
  return getFromLocalStorage(SEARCH_FORM_STORAGE_KEY);
};

/**
 * Validate if data has valid SearchFormData structure
 * @param data - The data to validate
 * @returns true if data is valid SearchFormData, false otherwise
 */
export const isValidSearchFormData = (data: unknown): data is SearchFormData => {
  if (!data || typeof data !== "object" || data === null) {
    return false;
  }

  const obj = data as Record<string, unknown>;
  
  return (
    obj.pickup !== undefined &&
    obj.drop !== undefined &&
    obj.dateTime !== undefined &&
    typeof obj.pickup === "object" &&
    obj.pickup !== null &&
    typeof obj.drop === "object" &&
    obj.drop !== null &&
    typeof obj.dateTime === "string"
  );
};

/**
 * Load and validate search form data from localStorage
 * @returns The saved search form data or null if not found/invalid
 */
export const loadSearchFormData = (): SearchFormData | null => {
  const data = loadDataFromStorage();
  return isValidSearchFormData(data) ? data : null;
};

/**
 * Clear search form data from localStorage
 * @returns true if cleared successfully, false otherwise
 */
export const clearSearchFormData = (): boolean => {
  return removeFromLocalStorage(SEARCH_FORM_STORAGE_KEY);
};
